import { TunaEditorContext, useLayout } from '@tunasong/plugin-lib'
import type { TunaEditor } from '@tunasong/plugin-lib'
import { ContentContainer, EntityEventsContainer, HBox, VBox, useDeviceInfo, useDimensions } from '@tunasong/ui-lib'
import type { ScrollState } from '@tunasong/ui-lib'
import { usePresenceSignaling } from '@tunasong/ws'
import type { ReactNode } from 'react'
import { useState } from 'react'
import Sidebar from '../sidebar/sidebar.js'
import type { ChromeProps } from './chrome.js'
import { EntityHeader } from './entity-header.js'
import { EntityLayout } from './entity-layout.js'
import styles from './entity-chrome.module.css'

export interface EntityChromeProps extends ChromeProps {
  // Override showSidebar from the layout
  showContainer?: boolean
  /** Extra controls rendered in the entity header's toolbar (right-aligned). */
  headerControls?: ReactNode
}

export function EntityChrome(props: EntityChromeProps) {
  const {
    entity,
    layoutName,
    children,
    showSidebar = true,
    showHeader = true,
    showContainer: providedShowContainer,
    headerControls,
  } = props

  const { isSmallOrSmaller } = useDimensions()

  usePresenceSignaling(entity)

  const { isMobile } = useDeviceInfo()

  const [scrollState, setScrollState] = useState<ScrollState>()
  const scrollHideHeader = scrollState === 'down' && isSmallOrSmaller

  const layout = useLayout({ entity, layoutName })

  const [editor, setEditor] = useState<TunaEditor | null>(null)

  const layoutContent = (
    <>
      {entity ? <EntityLayout entity={entity} layout={layout} onEditor={setEditor} /> : null}
      {children}
    </>
  )

  // If no layout is specidied, we show the container.
  // If layout is specified, we show the container if it's specified in the layout.
  const showContainer =
    typeof providedShowContainer !== 'undefined' ? providedShowContainer : layout?.container !== false

  const container = layout?.container ? layout.container : {}

  return (
    <TunaEditorContext.Provider value={editor}>
      <EntityEventsContainer entity={entity}>
        <VBox className={styles.outerBox}>
          <HBox className={styles.innerBox}>
            <VBox className={styles.scrollBox}>
              {showHeader && !scrollHideHeader ? (
                <EntityHeader
                  entity={entity}
                  layoutName={layoutName}
                  showSidebar={container?.showSidebar ?? true}
                  additionalControls={headerControls}
                />
              ) : null}

              {!showContainer ? (
                <VBox className={styles.nonContainer} style={{ paddingTop: showHeader ? 56 : 0 }}>
                  {layoutContent}
                </VBox>
              ) : (
                <ContentContainer
                  className={styles.container}
                  style={{
                    paddingTop: showHeader ? 56 : 0,
                    paddingBottom: isMobile ? 56 : 8,
                    ...container?.containerProps?.style,
                  }}
                  slots={{
                    EntityControls: layout?.slots?.EntityControls,
                  }}
                  entity={entity}
                  onScrollChange={setScrollState}
                  {...container?.containerProps}
                >
                  {layoutContent}
                </ContentContainer>
              )}
            </VBox>
            {showSidebar && showContainer ? <Sidebar entity={entity} /> : null}
          </HBox>
        </VBox>
      </EntityEventsContainer>
    </TunaEditorContext.Provider>
  )
}
